import { useEffect, useRef, useState } from 'react'

/**
 * Campo de busca que dispara sozinho depois que o usuário para de digitar.
 *
 * O guard de "só dispara se mudou de verdade" evita uma requisição redundante logo
 * depois que a página volta do servidor com o termo já aplicado — sem ele, cada
 * resposta agendaria uma busca nova com o mesmo texto.
 */
export function useDebouncedSearch(
  appliedTerm: string,
  onSearch: (term: string) => void,
  delayMs = 400,
) {
  const [term, setTerm] = useState(appliedTerm)

  // A callback muda de identidade a cada render da página (é uma arrow inline); guardar
  // numa ref evita reagendar o timer por causa disso e deixar o debounce nunca disparar.
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  })

  // Quando a navegação troca o termo aplicado (voltar do browser, "limpar filtros"),
  // o campo acompanha.
  useEffect(() => {
    setTerm(appliedTerm)
  }, [appliedTerm])

  useEffect(() => {
    if (term === appliedTerm) return

    const timer = setTimeout(() => onSearchRef.current(term), delayMs)
    return () => clearTimeout(timer)
  }, [term, appliedTerm, delayMs])

  return [term, setTerm] as const
}
